/* ==========================================================================
   Ozeret -- bottom sheets
   One shell, one switch. Sheets read and write `state.sheet` directly.
   ========================================================================== */

import { useState, type ReactNode } from 'react';
import { Field, Pick, SearchBox, Seg, Slider } from '../components/ui';
import { PhotoStrip } from '../components/Photos';
import { TownPicks } from '../components/TownPicks';
import { ZipField } from '../components/ZipField';
import { UI_LANGS, t } from '../lib/i18n';
import {
  JOB_CATALOG, LANGUAGES, TRAITS,
} from '../data/catalog';
import { AVAILABILITY, BOOKINGS, OCCURRENCES, RECURRING, REQUESTS, THREADS } from '../data/seed';
import { byId, cleaner, house, jobLabel, shortName } from '../lib/domain';
import { TODAY_ISO, addDays, fmtDate, fmtTime, money } from '../lib/time';
import {
  HOUSE_IDS, activeCleaner, activeHouse, addProperty, addTimeOff, blankJobFilter, blankPeopleFilter,
  closeSheet, commit,
  addCustomJob, counterOffer, hidePerson, invitedDates, reportCalendar, saveDays, saveJobNote,
  saveOccurrence, saveOnce, sendOffer, setLang, setProperty, state, toggleIn,
} from '../store';
import { JOB_SORTS } from './cleaner/WorkFeed';
import { PEOPLE_SORTS } from './house/FindCleaner';

/* --------------------------------------------------------------- the shell */

function Shell({
  title, children, foot,
}: { title: string; children: ReactNode; foot?: ReactNode }) {
  return (
    <div className="sheet-backdrop" onClick={closeSheet}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="grab" />
        <div className="sheet-head">
          <h3>{title}</h3>
          <button type="button" className="btn quiet sm" onClick={closeSheet}>
            {t('b_close')}
          </button>
        </div>
        <div className="sheet-body">{children}</div>
        <div className="sheet-foot">
          {foot ?? (
            <>
              <button
                type="button"
                className="btn quiet"
                onClick={() => {
                  state.filters.jobs = blankJobFilter();
                  state.filters.people = blankPeopleFilter();
                  commit();
                }}
              >
                {t('b_clear')}
              </button>
              <button type="button" className="btn grow" onClick={closeSheet}>
                {t('b_show')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const CloseFoot = () => (
  <button type="button" className="btn block" onClick={closeSheet}>{t('b_close')}</button>
);

/* ------------------------------------------------------------------ router */

export function Sheets() {
  const sheet = state.sheet;
  if (!sheet) return null;

  switch (sheet.name) {
    case 'lang': return <LangSheet />;
    case 'property': return <PropertySheet />;
    case 'jobsort': return <JobSortSheet />;
    case 'peoplesort': return <PeopleSortSheet />;
    case 'jobfilter': return <JobFilterSheet />;
    case 'peoplefilter': return <PeopleFilterSheet />;
    case 'pickdate': return <PickDateSheet />;
    case 'counter': return <CounterSheet />;
    case 'negotiate': return <NegotiateSheet />;
    case 'report': return <ReportSheet />;
    case 'note': return <NoteSheet />;
    case 'dayedit': return <DayEditSheet />;
    case 'timeoff': return <TimeOffSheet />;
    case 'customjob': return <CustomJobSheet />;
    case 'hide': return <HideSheet />;
    case 'once': return <OnceSheet />;
    case 'week': return <WeekSheet />;
    default: return null;
  }
}

/* --------------------------------------------------- one week of a series

   A repeating job is a rule, not a pile of identical weeks. This changes one
   of them: the oven this Tuesday, a note about the builders, or no visit at
   all that week. The rule is untouched, so every other Tuesday carries on
   exactly as before. */

function WeekSheet() {
  const sh = state.sheet!;
  const occ = OCCURRENCES.find((o) => o.recurringId === sh.id && o.date === sh.date);
  const series = RECURRING.find((r) => r.id === sh.id);
  const [ids, setIds] = useState<string[]>(occ ? occ.jobIds.slice() : []);
  const [note, setNote] = useState(occ?.note ?? '');
  if (!occ || !series) return null;

  const standing = house(occ.houseId).jobList.map((j) => j.jobId);
  const offered = [...new Set([...standing, ...ids])];
  const extras = JOB_CATALOG.map((j) => j.id).filter((id) => !offered.includes(id));

  return (
    <Shell
      title={fmtDate(occ.date, { long: true })}
      foot={
        <>
          <button type="button" className="btn quiet" onClick={closeSheet}>
            {t('b_cancel')}
          </button>
          <button
            type="button"
            className="btn grow"
            onClick={() => saveOccurrence(occ.recurringId, { date: occ.date, jobIds: ids, note })}
          >
            {t('b_save')}
          </button>
        </>
      }
    >
      <span className="hint">
        {t('sx_oneweek')}
      </span>

      {occ.changed && (
        <div className="note">{t('sx_differs')}</div>
      )}

      <Field label={t('sx_listweek')}>
        <div className="chips">
          {offered.map((id) => (
            <Pick
              key={id}
              label={jobLabel(id)}
              on={ids.includes(id)}
              onClick={() => { toggleIn(ids, id); setIds(ids.slice()); }}
            />
          ))}
        </div>
      </Field>

      <Field label={t('sx_addweek')}>
        <div className="chips">
          {extras.map((id) => (
            <Pick key={id} label={jobLabel(id)} on={false} onClick={() => setIds([...ids, id])} />
          ))}
        </div>
      </Field>

      <Field label={t('sx_sayweek')}>
        <textarea
          className="textarea"
          placeholder={t('sx_awayph')}
          value={note}
          maxLength={2000}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>

      <button
        type="button"
        className="btn danger block"
        onClick={() => saveOccurrence(occ.recurringId, { date: occ.date, skipped: !occ.skipped })}
      >
        {occ.skipped ? 'Put this week back on' : 'No visit this week'}
      </button>

      <div className="note">
        {shortName(cleaner(occ.cleanerId).name)} is told in your conversation, so nothing
        moves under them.
      </div>
    </Shell>
  );
}

/* ------------------------------------------------------- just this time

   The oven this Tuesday, the spare room while the guests are here. It changes
   the visit and nothing else -- the home's standing list is untouched, which
   is what stops a one-off quietly becoming permanent. */

function OnceSheet() {
  const b = byId(BOOKINGS, state.sheet!.id ?? '');
  const standing = b ? house(b.houseId).jobList.map((j) => j.jobId) : [];
  const [ids, setIds] = useState<string[]>(b ? b.jobIds.slice() : []);
  const [note, setNote] = useState(b?.onceNote ?? '');
  if (!b) return null;

  /* Everything they normally have, plus anything already on this visit. */
  const offered = [...new Set([...standing, ...ids])];
  const extras = JOB_CATALOG.map((j) => j.id).filter((id) => !offered.includes(id));

  return (
    <Shell
      title={
        (b.recurringId ? 'Just for ' : 'Changes for ') + fmtDate(b.date, { long: true })
      }
      foot={
        <>
          <button type="button" className="btn quiet" onClick={closeSheet}>
            {t('b_cancel')}
          </button>
          <button
            type="button"
            className="btn grow"
            onClick={() => saveOnce(b.id, { note, jobIds: ids })}
          >
            {t('b_save')}
          </button>
        </>
      }
    >
      <span className="hint">
        {b.recurringId
          ? 'This changes this visit only — the rest of the series carries on unchanged. Your saved job list stays exactly as it is.'
          : 'Your saved job list stays exactly as it is. This is only about this job.'}
      </span>

      <Field label={t('sx_listtime')}>
        <div className="chips">
          {offered.map((id) => (
            <Pick
              key={id}
              label={jobLabel(id)}
              on={ids.includes(id)}
              onClick={() => { toggleIn(ids, id); setIds(ids.slice()); }}
            />
          ))}
        </div>
      </Field>

      <Field label={t('sx_addtime')}>
        <div className="chips">
          {extras.map((id) => (
            <Pick key={id} label={jobLabel(id)} on={false} onClick={() => setIds([...ids, id])} />
          ))}
        </div>
      </Field>

      <Field label={t('sx_sayvisit')}>
        <textarea
          className="textarea"
          placeholder={t('sx_dustph')}
          value={note}
          maxLength={2000}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>

      {/* Attached to the visit, so it goes when the visit is in the past --
          unlike a picture on the job list, which is about the home. */}
      <Field label={t('sx_picsvisit')}>
        <PhotoStrip kind="booking" ownerId={b.id} canAdd />
      </Field>

      <div className="note">
        {t('sx_seeschange')}
      </div>
    </Shell>
  );
}

/* --------------------------------------------------------- stepping away

   Worth a sheet rather than a straight tap: it is quiet and undoable, but
   people should know exactly how quiet. Nobody is told, and nothing already
   agreed is cancelled. */

function HideSheet() {
  const sh = state.sheet!;
  const who = sh.who ?? 'cleaner';
  const label = sh.name2 ?? 'them';
  const target = who === 'house' ? { houseId: sh.id } : { cleanerId: sh.id };

  return (
    <Shell
      title={t('sx_stopseeing') + label + '?'}
      foot={
        <>
          <button type="button" className="btn quiet" onClick={closeSheet}>
            {t('b_cancel')}
          </button>
          <button
            type="button"
            className="btn grow warn"
            onClick={() => hidePerson(target, label)}
          >
            Hide {label}
          </button>
        </>
      }
    >
      <div className="col" style={{ gap: 10 }}>
        <span className="sub">
          {who === 'house'
            ? 'Their work stops appearing in your search, and you will not be shown to them.'
            : 'They stop appearing when you look for someone, and any application of theirs is withdrawn.'}
        </span>
        <span className="sub">
          <b>{t('sx_nottold')}</b> Nothing you have already agreed is cancelled — a booking
          that exists still stands.
        </span>
        <span className="sub">
          {who === 'house'
            ? 'They can still invite you directly. You will not see the invitation while they are hidden — it waits, and appears if you undo this.'
            : 'You can undo this at any time in Settings.'}
        </span>
      </div>
    </Shell>
  );
}

/* ------------------------------------------------------------------ simple */

function LangSheet() {
  return (
    <Shell title={t('h_language')} foot={<CloseFoot />}>
      <div className="chips">
        {UI_LANGS.map((l) => (
          <Pick key={l.id} label={l.native} on={state.lang === l.id} onClick={() => setLang(l.id)} />
        ))}
      </div>
      <span className="hint">
        {t('tx_lang_hint')}
      </span>
    </Shell>
  );
}

function PropertySheet() {
  /* The switcher was the only thing here, which meant a household with one
     property saw a list of one and no way to get a second. Adding one is now
     part of the same sheet -- it is where somebody looking for it would
     look. */
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [zip, setZip] = useState('');
  const ready = name.trim().length >= 2;

  return (
    <Shell title={t('h_whichproperty')} foot={<CloseFoot />}>
      <div className="chips">
        {HOUSE_IDS.map((id) => (
          <Pick
            key={id}
            label={house(id).displayName}
            on={state.houseId === id}
            onClick={() => setProperty(id)}
          />
        ))}
      </div>
      <span className="hint">{t('sx_perproperty')}</span>

      {adding ? (
        <>
          <Field label={t('sx_callit')}>
            <input
              className="input"
              placeholder={t('sx_callitph')}
              value={name}
              autoFocus
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <ZipField
            label={t('h_postal')}
            value={zip}
            onChange={setZip}
            hint="Cleaners see the town, never the address."
          />
          <span className="hint">
            Everything else — rooms, pets, parking, the job list — you fill in afterwards
            in Edit this home. A second property is a different building, so nothing is
            copied across from the first.
          </span>
          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn quiet"
              onClick={() => { setAdding(false); setName(''); setZip(''); }}
            >
              {t('b_cancel')}
            </button>
            <button
              type="button"
              className="btn grow"
              disabled={!ready || state.busy}
              onClick={() => void addProperty(name.trim(), zip)}
            >
              {t('sx_addthis')}
            </button>
          </div>
        </>
      ) : (
        <button type="button" className="btn ghost block" onClick={() => setAdding(true)}>
          {t('sx_addanother')}
        </button>
      )}
    </Shell>
  );
}

function JobSortSheet() {
  return (
    <Shell title={t('sortby')} foot={<CloseFoot />}>
      <div className="chips">
        {JOB_SORTS.map((s) => (
          <Pick
            key={s.id}
            label={t(s.key)}
            on={state.filters.jobs.sort === s.id}
            onClick={() => { state.filters.jobs.sort = s.id; closeSheet(); }}
          />
        ))}
      </div>
    </Shell>
  );
}

function PeopleSortSheet() {
  return (
    <Shell title={t('sortby')} foot={<CloseFoot />}>
      <div className="chips">
        {PEOPLE_SORTS.map((s) => (
          <Pick
            key={s.id}
            label={t(s.key)}
            on={state.filters.people.sort === s.id}
            onClick={() => { state.filters.people.sort = s.id; closeSheet(); }}
          />
        ))}
      </div>
    </Shell>
  );
}

/* ------------------------------------------------------------------ filters */

const JOB_GROUPS = [...new Set(JOB_CATALOG.map((j) => j.group))];

function JobFilterSheet() {
  const f = state.filters.jobs;
  const flip = (k: 'areas' | 'tags' | 'langs', v: string) => { toggleIn(f[k], v); commit(); };

  return (
    <Shell title={t('filters')}>
      <Field label="Search" hint="A home's name, or a word from what they wrote.">
        <SearchBox
          value={f.q}
          onChange={(v) => { f.q = v; commit(); }}
          placeholder={t('sx_homeorjob')}
        />
      </Field>

      <Field label="Date">
        <input
          className="input"
          type="date"
          value={f.date}
          onChange={(e) => { f.date = e.target.value; commit(); }}
        />
        {f.date && (
          <button
            type="button"
            className="btn quiet sm"
            onClick={() => { f.date = ''; commit(); }}
          >
            {t('anyday')}
          </button>
        )}
      </Field>

      <Field label={t('areas')}>
        <TownPicks
          zip={activeCleaner()?.baseZip ?? ''}
          anchors={activeCleaner()?.areas ?? []}
          chosen={f.areas}
          onFlip={(id) => flip('areas', id)}
        />
      </Field>

      <Field label={t('sx_payatleast') + (f.minRate ? money(f.minRate) : 'any')}>
        <Slider
          value={f.minRate || 15}
          onChange={(v) => { f.minRate = v === 15 ? 0 : v; commit(); }}
        />
      </Field>

      <Field label={t('cp_minhours')}>
        <Seg
          options={[
            { id: '0', label: 'Any' }, { id: '3', label: '3h+' },
            { id: '4', label: '4h+' }, { id: '5', label: '5h+' },
          ]}
          value={String(f.minHours)}
          onChange={(v) => { f.minHours = Number(v); commit(); }}
        />
      </Field>

      <Field label={t('sx_homespeaks')}>
        <div className="chips">
          {LANGUAGES.map((l) => (
            <Pick key={l.id} label={l.label} on={f.langs.includes(l.id)}
              onClick={() => flip('langs', l.id)} />
          ))}
        </div>
      </Field>

      {JOB_GROUPS.map((g) => (
        <Field label={g} key={g}>
          <div className="chips">
            {JOB_CATALOG.filter((j) => j.group === g).map((j) => (
              <Pick key={j.id} label={jobLabel(j.id)} on={f.tags.includes(j.id)}
                onClick={() => flip('tags', j.id)} />
            ))}
          </div>
        </Field>
      ))}
    </Shell>
  );
}

function PeopleFilterSheet() {
  const f = state.filters.people;
  const flip = (k: 'areas' | 'tags' | 'langs', v: string) => { toggleIn(f[k], v); commit(); };

  return (
    <Shell title={t('filters')}>
      <Field label={t('sx_searchname')} hint="Looking for somebody in particular.">
        <SearchBox
          value={f.q}
          onChange={(v) => { f.q = v; commit(); }}
          placeholder={t('sx_cleanername')}
        />
      </Field>

      <Field label={t('sx_freeon')}>
        <input
          className="input"
          type="date"
          value={f.date}
          onChange={(e) => { f.date = e.target.value; commit(); }}
        />
        {f.date && (
          <button
            type="button"
            className="btn quiet sm"
            onClick={() => { f.date = ''; commit(); }}
          >
            {t('anyday')}
          </button>
        )}
      </Field>

      <Field label={`Rate between ${money(f.rateMin)} and ${money(f.rateMax)}`}>
        <Slider
          value={f.rateMin}
          onChange={(v) => {
            f.rateMin = Math.min(v, f.rateMax);
            commit();
          }}
        />
        <Slider
          value={f.rateMax}
          onChange={(v) => {
            f.rateMax = Math.max(v, f.rateMin);
            commit();
          }}
        />
      </Field>

      <Field label={t('sx_worksin')}>
        <TownPicks
          zip={activeHouse().zip ?? ''}
          anchors={[activeHouse().area]}
          chosen={f.areas}
          onFlip={(id) => flip('areas', id)}
        />
      </Field>

      <Field label={t('sx_speaks')}>
        <div className="chips">
          {LANGUAGES.map((l) => (
            <Pick key={l.id} label={l.label} on={f.langs.includes(l.id)}
              onClick={() => flip('langs', l.id)} />
          ))}
        </div>
      </Field>

      <Field
        label={t('sx_cleaneris')}
        hint="Many homes have a preference. It is filtered, never shown as a judgement."
      >
        <Seg
          options={[
            { id: 'any', label: 'Either' },
            { id: 'female', label: 'A woman' },
            { id: 'male', label: 'A man' },
          ]}
          value={f.gender}
          onChange={(v) => { f.gender = v; commit(); }}
        />
      </Field>

      <Field label={t('sx_ratedatleast')}>
        <Seg
          options={[
            { id: '0', label: 'Any' }, { id: '4', label: '4★' },
            { id: '4.5', label: '4.5★' }, { id: '4.8', label: '4.8★' },
          ]}
          value={String(f.minRating)}
          onChange={(v) => { f.minRating = Number(v); commit(); }}
        />
      </Field>

      <Field label={t('sx_howtheywork')}>
        <div className="chips">
          {TRAITS.map((x) => (
            <Pick key={x.id} label={x.label} on={f.tags.includes(x.id)}
              onClick={() => flip('tags', x.id)} />
          ))}
        </div>
      </Field>

      {JOB_GROUPS.map((g) => (
        <Field label={t('specialties') + ' — ' + g} key={g}>
          <div className="chips">
            {JOB_CATALOG.filter((j) => j.group === g).map((j) => (
              <Pick key={j.id} label={jobLabel(j.id)} on={f.tags.includes(j.id)}
                onClick={() => flip('tags', j.id)} />
            ))}
          </div>
        </Field>
      ))}
    </Shell>
  );
}

function PickDateSheet() {
  const f = state.filters.people;
  return (
    <Shell
      title={t('pickdate')}
      foot={
        <>
          <button
            type="button"
            className="btn quiet"
            onClick={() => { f.date = ''; closeSheet(); }}
          >
            {t('anyday')}
          </button>
          <button type="button" className="btn grow" onClick={closeSheet}>{t('b_show')}</button>
        </>
      }
    >
      <Field label={t('sx_showfreeon')}>
        <input
          className="input"
          type="date"
          value={f.date || TODAY_ISO}
          onChange={(e) => { f.date = e.target.value; commit(); }}
        />
      </Field>
      <div className="chips">
        {Array.from({ length: 7 }, (_, i) => addDays(TODAY_ISO, i)).map((iso) => (
          <Pick
            key={iso}
            label={fmtDate(iso)}
            on={f.date === iso}
            onClick={() => { f.date = iso; commit(); }}
          />
        ))}
      </div>
    </Shell>
  );
}

/* ------------------------------------------------------------------ money */

function CounterSheet() {
  const r = byId(REQUESTS, state.sheet!.id ?? '');
  const c = activeCleaner();
  /* Starts above what they offered: a counter is asking for more. It used to
     open at her usual rate, which on a $32 job meant offering $30. */
  const v = state.sheet!.rate ?? Math.max(c?.rate ?? 30, (r?.rate ?? 0) + 1);
  const [msg, setMsg] = useState('');

  /* Only a cleaner can counter an offer. Guarded rather than assumed --
     this same assumption has broken two screens already. */
  if (!r || !c) return null;

  return (
    <Shell
      title={t('b_offer')}
      foot={
        <button
          type="button"
          className="btn block"
          disabled={v <= r.rate}
          onClick={() => counterOffer(r.id, v, msg)}
        >
          {v <= r.rate ? 'Ask for more than ' + money(r.rate) : t('b_send')}
        </button>
      }
    >
      <div className="note">
        They offered {money(r.rate)}{t('perhour')} for {r.hours} {t('hours')}. Your usual rate
        is {money(c.rate)}.
      </div>
      <Field label={t('sx_yourrate') + money(v)}>
        <Slider value={v} onChange={(n) => { state.sheet!.rate = n; commit(); }} />
      </Field>
      <div className="row between">
        <span className="sub">{t('sx_theywouldpay')}</span>
        <span className="rate">{money(v * r.hours)}</span>
      </div>
      <Field label={t('sx_saywhy')}>
        <textarea
          className="textarea"
          placeholder="e.g. It is a big list for 5 hours and the oven takes an hour on its own."
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
        />
      </Field>
    </Shell>
  );
}

function NegotiateSheet() {
  const sheet = state.sheet!;
  /* Whoever is proposing, the starting number is the cleaner's own rate — and
     that cleaner comes from the conversation, not from the session. A home has
     no `cleanerId` of its own, which is what used to break this screen. */
  const th = byId(THREADS, sheet.id ?? '');
  const c = th ? cleaner(th.cleanerId) : undefined;
  const v = sheet.rate ?? c?.rate ?? 30;
  const hrs = sheet.hours ?? 4;
  const [note, setNote] = useState('');

  if (!th) return null;

  return (
    <Shell
      title={t('b_negotiate')}
      foot={
        <button
          type="button"
          className="btn block"
          onClick={() => sendOffer(sheet.id!, v, hrs, note)}
        >
          {t('b_send')}
        </button>
      }
    >
      <div className="note">
        Sending a price here puts it on the record. If they accept, that is the agreed rate and
        it shows on the booking — no arguing later about what was said.
      </div>
      {c && (
        <span className="hint">
          {c.name.split(' ')[0]}&apos;s usual rate is {money(c.rate)}{t('perhour')}.
        </span>
      )}
      <Field label={t('pr_rateperhour') + money(v)}>
        <Slider value={v} onChange={(n) => { sheet.rate = n; commit(); }} />
      </Field>
      <Field label={t('hours')}>
        <div className="stepper">
          <button type="button" onClick={() => { sheet.hours = Math.max(1, hrs - 0.5); commit(); }}>−</button>
          <span className="val">{hrs}</span>
          <button type="button" onClick={() => { sheet.hours = hrs + 0.5; commit(); }}>+</button>
        </div>
      </Field>
      <div className="row between">
        <span className="sub">{t('total')}</span>
        <span className="rate">{money(v * hrs)}</span>
      </div>
      <Field label={t('sx_addnote')}>
        <textarea
          className="textarea"
          placeholder={t('sx_optional')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
    </Shell>
  );
}

/* ------------------------------------------------------------------ other */

function ReportSheet() {
  const cleanerId = state.sheet!.id!;
  const days = invitedDates(cleanerId);
  const [picked, setPicked] = useState<string[]>([]);
  const [detail, setDetail] = useState('');
  const [day, setDay] = useState(days[0] ?? '');

  /* Nothing to report about somebody you never asked. Saying so plainly beats
     a form that submits and is then refused by the server. */
  if (!days.length) {
    return (
      <Shell title={t('h_reportcal')} foot={<CloseFoot />}>
        <div className="note">
          {t('sx_onlyinvited')}
          <br />
          <br />
          There is no such day in the last fortnight, so there is nothing to report here.
          If they let you down on a job you booked, say so in your review instead — that is
          the thing other homes read.
        </div>
      </Shell>
    );
  }

  const options = [
    'Said they were busy after all',
    'Never replied',
    'Cancelled last minute',
  ];
  return (
    <Shell
      title={t('h_reportcal')}
      foot={
        <button
          type="button"
          className="btn block"
          disabled={picked.length === 0 || !day}
          onClick={() => reportCalendar(cleanerId, day, picked, detail)}
        >
          {t('b_send')}
        </button>
      }
    >
      <div className="note">
        Use this when someone&apos;s calendar showed a slot as open, you asked for it, and they
        told you privately they were not free.
      </div>
      <Field label={t('sx_whichday')}>
        <div className="chips">
          {days.map((d) => (
            <Pick
              key={d}
              label={fmtDate(d, { long: true })}
              on={day === d}
              onClick={() => setDay(d)}
            />
          ))}
        </div>
      </Field>
      <Field label={t('sx_whathappened')}>
        <div className="chips">
          {options.map((o) => (
            <Pick
              key={o}
              label={o}
              on={picked.includes(o)}
              onClick={() =>
                setPicked((p) => (p.includes(o) ? p.filter((x) => x !== o) : [...p, o]))
              }
            />
          ))}
        </div>
      </Field>
      <Field label={t('sx_anythingadd')}>
        <textarea
          className="textarea"
          placeholder={t('sx_optional')}
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
        />
      </Field>
      <div className="note warm">
        One report does nothing on its own. After three, the profile is flagged and stops
        appearing in search until it is fixed.
      </div>
    </Shell>
  );
}

function NoteSheet() {
  const i = state.sheet!.i!;
  const h = house(state.houseId);
  const [text, setText] = useState(h.jobList[i]?.note || '');

  return (
    <Shell
      title={jobLabel(h.jobList[i].jobId)}
      foot={
        <button type="button" className="btn block" onClick={() => saveJobNote(i, text)}>
          {t('b_save')}
        </button>
      }
    >
      <Field label={t('sx_yournote')}>
        <textarea
          className="textarea"
          placeholder={t('sx_toldonce')}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </Field>
      <div className="note">
        {t('sx_notesshown')}
      </div>
    </Shell>
  );
}

/* The heart of the availability fixes: pick days, then set them all at once. */
function DayEditSheet() {
  const sheet = state.sheet!;
  const days = state.selDays.slice().sort();
  const mode = sheet.mode || 'available';
  const from = sheet.from || '14:00';
  const to = sheet.to || '16:00';
  const wFrom = sheet.wFrom || '09:00';
  const wTo = sheet.wTo || '17:00';
  const [reason, setReason] = useState('');
  const many = days.length > 1;
  const title = days.length === 1 ? fmtDate(days[0], { long: true }) : days.length + ' days selected';

  return (
    <Shell
      title={title}
      foot={
        <>
          <button
            type="button"
            className="btn quiet"
            onClick={() => { state.selDays = []; closeSheet(); }}
          >
            {t('b_cancel')}
          </button>
          <button
            type="button"
            className="btn grow"
            onClick={() => saveDays(days, mode, { from, to, wFrom, wTo, reason })}
          >
            {t('b_save')}
          </button>
        </>
      }
    >
      {many && (
        <div className="chips">
          {days.map((d) => <span className="chip tile" key={d}>{fmtDate(d)}</span>)}
        </div>
      )}

      <Field label={t('sx_thesedays')}>
        <Seg
          options={[
            { id: 'available', label: t('available') },
            { id: 'block', label: 'Block hours' },
            { id: 'off', label: t('unavailable') },
          ]}
          value={mode}
          onChange={(v) => { sheet.mode = v as typeof mode; commit(); }}
        />
      </Field>

      {mode === 'block' && (
        <>
          <Field label={t('sx_blocked')}>
            <div className="field-row">
              <input
                className="input"
                type="time"
                value={from}
                onChange={(e) => { sheet.from = e.target.value; commit(); }}
              />
              <input
                className="input"
                type="time"
                value={to}
                onChange={(e) => { sheet.to = e.target.value; commit(); }}
              />
            </div>
          </Field>
          <Field label={t('reason') + ' (only you see this)'}>
            <input
              className="input"
              placeholder={t('sx_schoolph')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          <div className="note">
            You stay bookable on {many ? 'these days' : 'this day'} — just not between{' '}
            {fmtTime(from)} and {fmtTime(to)}. Homes see the hours that are left.
          </div>
        </>
      )}

      {mode === 'off' && (
        <>
          <Field label={t('reason') + ' (only you see this)'}>
            <input
              className="input"
              placeholder={t('sx_awayph2')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          <div className="note">
            Homes will not see you as available at all on {many ? 'these days' : 'this day'}.
          </div>
        </>
      )}

      {mode === 'available' && (
        <>
          <Field label={t('sx_workinghours')}>
            <div className="field-row">
              <input
                className="input"
                type="time"
                value={wFrom}
                onChange={(e) => { sheet.wFrom = e.target.value; commit(); }}
              />
              <input
                className="input"
                type="time"
                value={wTo}
                onChange={(e) => { sheet.wTo = e.target.value; commit(); }}
              />
            </div>
          </Field>
          <div className="note">
            Overrides your usual week for {many ? 'these days' : 'this day'}, and clears
            anything blocked.
          </div>
        </>
      )}
    </Shell>
  );
}

/** For the things a catalog cannot anticipate: the fish tank, the sukkah, the
 *  particular way somebody wants their sofa cushions. */
function CustomJobSheet() {
  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');

  return (
    <Shell
      title={t('h_addown')}
      foot={
        <button
          type="button"
          className="btn block"
          disabled={label.trim().length < 2}
          onClick={() => addCustomJob(label, note)}
        >
          {t('b_add')}
        </button>
      }
    >
      <Field label={t('sx_whatcalled')} hint="Short and plain — a cleaner reads this on a card.">
        <input
          className="input"
          placeholder={t('sx_fishtankph')}
          value={label}
          maxLength={60}
          autoFocus
          onChange={(e) => setLabel(e.target.value)}
        />
      </Field>
      <Field label={t('sx_shouldknow')}>
        <textarea
          className="textarea"
          placeholder={t('sx_optional')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
      <div className="note">
        Your own jobs sit on your list beside the catalog ones. Cleaners will not find
        you by searching for it, because only your home uses that name.
      </div>
    </Shell>
  );
}

function TimeOffSheet() {
  const [from, setFrom] = useState(addDays(TODAY_ISO, 14));
  const [to, setTo] = useState(addDays(TODAY_ISO, 21));
  const [why, setWhy] = useState('');
  const av = AVAILABILITY[state.cleanerId];

  return (
    <Shell
      title={t('timeoff')}
      foot={
        <button
          type="button"
          className="btn block"
          onClick={() => addTimeOff(from, to, why)}
          disabled={!av}
        >
          {t('b_block')}
        </button>
      }
    >
      <div className="field-row">
        <Field label={t('from')}>
          <input
            className="input"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </Field>
        <Field label={t('to')}>
          <input
            className="input"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </Field>
      </div>
      <Field label={t('reason') + ' (only you see this)'}>
        <input
          className="input"
          placeholder={t('sx_awayph3')}
          value={why}
          onChange={(e) => setWhy(e.target.value)}
        />
      </Field>
      <div className="note">
        For part of a day, close this and tap the day in the calendar instead — you can block
        just a few hours there.
      </div>
    </Shell>
  );
}
